import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { StoreOrder } from '../models';
import { storeService } from '../services/storeService';
import { useInitEffect } from '../hooks/useInitEffect';
import { useAccountSession } from '../hooks/useAccountSession';
import { useReviewableProducts } from '../hooks/useReviewableProducts';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { AccountShell } from '../components/profile/AccountShell';
import { OrderCard } from '../components/profile/OrderCard';
import { OrderDetailsModal } from '../components/profile/OrderDetailsModal';
import { EditOrderModal } from '../components/profile/EditOrderModal';
import { ReviewProductsModal } from '../components/profile/ReviewProductsModal';
import { isOrderInProgress } from '../components/profile/orderStatus';
import { extractApiError } from '../utils/orderFormat';

type LoadState = 'loading' | 'ready' | 'error';

const PANEL_CLASS = 'bg-white rounded-2xl border border-champagne-dark shadow-sm';

function OrdersSkeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      {[0, 1].map((key) => (
        <div key={key} className={`${PANEL_CLASS} p-5 animate-pulse`}>
          <div className="flex justify-between">
            <div className="h-4 w-28 rounded bg-champagne" />
            <div className="h-4 w-20 rounded bg-champagne" />
          </div>
          <div className="mt-3 h-5 w-40 rounded-full bg-champagne-light" />
          <div className="mt-5 flex gap-3">
            <div className="w-12 h-12 rounded-lg bg-champagne" />
            <div className="flex-1 space-y-2 pt-1">
              <div className="h-3 w-2/3 rounded bg-champagne" />
              <div className="h-3 w-1/3 rounded bg-champagne-light" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function OrdersPage() {
  const { isAuthenticated, customerName, unauthorized, handleRequestError, signOut } = useAccountSession();
  const [orders, setOrders] = useState<StoreOrder[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [actionError, setActionError] = useState('');
  const [cancelingOrderId, setCancelingOrderId] = useState<number | null>(null);
  const [viewingOrder, setViewingOrder] = useState<StoreOrder | null>(null);
  const [editingOrder, setEditingOrder] = useState<StoreOrder | null>(null);
  const [reviewOrderId, setReviewOrderId] = useState<number | null>(null);
  const { reviewableByOrder, markProductReviewed } = useReviewableProducts(orders);
  const { confirm, ConfirmDialogElement } = useConfirm();

  const loadOrders = useCallback(async () => {
    setLoadState('loading');
    try {
      setOrders(await storeService.getOrders());
      setLoadState('ready');
    } catch (error) {
      if (!handleRequestError(error)) setLoadState('error');
    }
  }, [handleRequestError]);

  const refreshOrders = useCallback(async () => {
    setOrders(await storeService.getOrders());
  }, []);

  useInitEffect(() => {
    loadOrders();
  });

  async function handleCancel(order: StoreOrder) {
    const confirmed = await confirm({
      title: `Cancelar pedido #${order.id}?`,
      message: 'Tem certeza que deseja cancelar este pedido? Essa ação não pode ser desfeita.',
      confirmLabel: 'Sim, cancelar pedido',
      cancelLabel: 'Manter pedido',
      danger: true,
    });
    if (!confirmed) return;

    setCancelingOrderId(order.id);
    setActionError('');
    try {
      await storeService.cancelOrder(order.id);
      await refreshOrders();
    } catch (error) {
      if (!handleRequestError(error)) {
        setActionError(extractApiError(error, `Não foi possível cancelar o pedido #${order.id}. Tente de novo.`));
      }
    } finally {
      setCancelingOrderId(null);
    }
  }

  if (!isAuthenticated && !unauthorized) return null;

  const inProgress = orders.filter(isOrderInProgress);
  const previous = orders.filter((o) => !isOrderInProgress(o));
  const groups = [
    { key: 'andamento', title: 'Em andamento', orders: inProgress },
    { key: 'anteriores', title: inProgress.length > 0 ? 'Pedidos anteriores' : 'Seus pedidos', orders: previous },
  ].filter((group) => group.orders.length > 0);

  return (
    <AccountShell
      title="Meus pedidos"
      description="Acompanhe suas compras e o que falta em cada uma."
      customerName={customerName}
      unauthorized={unauthorized}
      onSignOut={signOut}
    >
      {actionError && (
        <div role="alert" className="mb-4 flex items-start justify-between gap-3 rounded-2xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-text-dark">
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError('')} className="font-medium text-chocolate-dark hover:underline underline-offset-4 shrink-0">
            Fechar
          </button>
        </div>
      )}

      {loadState === 'loading' && (
        <>
          <span className="sr-only" role="status">Carregando pedidos</span>
          <OrdersSkeleton />
        </>
      )}

      {loadState === 'error' && (
        <div className={`${PANEL_CLASS} px-6 py-10 text-center`}>
          <p className="font-semibold text-text-dark">Não conseguimos carregar seus pedidos</p>
          <p className="text-sm text-text-medium mt-1">Verifique sua conexão e tente de novo.</p>
          <button
            type="button"
            onClick={loadOrders}
            className="mt-5 min-h-10 px-5 text-sm font-semibold text-white bg-chocolate-dark hover:bg-chocolate-deep rounded-full transition-colors"
          >
            Tentar de novo
          </button>
        </div>
      )}

      {loadState === 'ready' && orders.length === 0 && (
        <div className={`${PANEL_CLASS} px-6 py-12 text-center`}>
          <p className="font-display text-lg font-semibold text-text-dark">Você ainda não fez nenhum pedido</p>
          <p className="text-sm text-text-medium mt-1 max-w-sm mx-auto">
            Quando fizer sua primeira compra, ela aparece aqui com o status e o próximo passo.
          </p>
          <Link
            to="/catalogo"
            className="inline-flex items-center mt-5 min-h-10 px-5 text-sm font-semibold text-white bg-chocolate-dark hover:bg-chocolate-deep rounded-full transition-colors"
          >
            Ver o catálogo
          </Link>
        </div>
      )}

      {loadState === 'ready' &&
        groups.map((group) => (
          <section key={group.key} aria-labelledby={`grupo-${group.key}`} className="mb-8 last:mb-0">
            <h2 id={`grupo-${group.key}`} className="mb-3 flex items-baseline gap-2 text-base font-semibold text-text-dark">
              {group.title}
              <span className="text-sm font-normal text-text-medium tabular-nums">{group.orders.length}</span>
            </h2>
            <div className="space-y-4">
              {group.orders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  pendingReviews={reviewableByOrder[order.id]?.length ?? 0}
                  canceling={cancelingOrderId === order.id}
                  onView={() => setViewingOrder(order)}
                  onReview={() => setReviewOrderId(order.id)}
                  onEdit={() => setEditingOrder(order)}
                  onCancel={() => handleCancel(order)}
                />
              ))}
            </div>
          </section>
        ))}

      <OrderDetailsModal order={viewingOrder} onClose={() => setViewingOrder(null)} />

      {editingOrder && (
        <EditOrderModal order={editingOrder} onClose={() => setEditingOrder(null)} onSaved={refreshOrders} />
      )}

      {reviewOrderId !== null && (
        <ReviewProductsModal
          open
          onClose={() => setReviewOrderId(null)}
          orderId={reviewOrderId}
          products={reviewableByOrder[reviewOrderId] ?? []}
          onReviewed={(productId) => markProductReviewed(reviewOrderId, productId)}
        />
      )}

      {ConfirmDialogElement}
    </AccountShell>
  );
}
